import { useState } from "react";
import { liveComments } from "@/mocks/expose";

type CommentItem = { name: string; tag: string; text: string; likes: number; replies: number };

const cannedReplies = [
  "同意，时间线一旦排开，很多说法就站不住了。坐等编辑部更新。",
  "我补个角度：那段公开回应里根本没有正面解释出资来源，越是打太极越可疑。",
];

export default function CommentModule() {
  const [list, setList] = useState<CommentItem[]>(liveComments);
  const [liked, setLiked] = useState<Record<number, boolean>>({});
  const [openReply, setOpenReply] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [author, setAuthor] = useState("");

  const toggleLike = (idx: number) => {
    setLiked((s) => {
      const was = !!s[idx];
      setList((prev) =>
        prev.map((c, i) => (i === idx ? { ...c, likes: c.likes + (was ? -1 : 1) } : c))
      );
      return { ...s, [idx]: !was };
    });
  };

  const submitComment = () => {
    if (!draft.trim()) return;
    const item: CommentItem = {
      name: author.trim() || "匿名深挖者",
      tag: "新读者",
      text: draft.trim(),
      likes: 0,
      replies: 0,
    };
    setList((prev) => [item, ...prev]);
    setDraft("");
    setAuthor("");
  };

  return (
    <section id="comments" className="border-y border-accent-200/70 bg-accent-100/40">
      <div className="mx-auto max-w-page px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-8 lg:grid-cols-5">
          {/* 参与入口 */}
          <div className="lg:col-span-2">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-accent-700">
              <i className="ri-discuss-line text-base"></i>
              Community · 读者热议
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              你来补充，我们把线索接上
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              评论区是深挖的第二现场。任何与主题相关的公开信息、旧报道或时间线疑点，都可以在这里留档，编辑部会挑重点复核。
            </p>

            <div className="mt-5 rounded-lg border border-accent-200/70 bg-background-50 p-4 md:p-5">
              <p className="text-sm font-semibold text-foreground-900">写下你的线索或看法</p>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="只贴可公开查证的信息，勿涉及个人隐私…"
                className="mt-3 w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400"
              ></textarea>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  maxLength={20}
                  placeholder="显示昵称（可留空）"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400 sm:w-44"
                />
                <button
                  type="button"
                  onClick={submitComment}
                  className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 sm:ml-auto"
                >
                  <i className="ri-send-plane-line"></i> 发布参与
                </button>
              </div>
              <p className="mt-2 text-[11px] text-foreground-400">发布即代表你同意本站的发布规范。</p>
            </div>
          </div>

          {/* 评论流 */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground-900">
                <i className="ri-flashlight-line mr-1 text-accent-600"></i>
                高赞补刀 · {list.length} 条
              </span>
              <span className="flex items-center gap-1 text-xs text-accent-700">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-500 opacity-75"></span>
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-600"></span>
                </span>
                实时热议中
              </span>
            </div>

            <div className="mt-4 space-y-3">
              {list.map((c, idx) => {
                const isLiked = !!liked[idx];
                return (
                  <article key={`${c.name}-${idx}`} className="rounded-lg border border-background-200/80 bg-background-50 p-4">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-500 text-xs font-bold text-background-50">
                        {c.name.slice(0, 1)}
                      </span>
                      <span className="text-sm font-semibold text-foreground-900">{c.name}</span>
                      <span className="rounded bg-background-100 px-1.5 py-0.5 text-[10px] text-foreground-500">
                        {c.tag}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-foreground-800">{c.text}</p>

                    {openReply === idx && (
                      <ul className="mt-3 space-y-2 border-l-2 border-accent-200 pl-3">
                        {cannedReplies.map((r) => (
                          <li key={r} className="text-xs leading-5 text-foreground-600">
                            <span className="font-semibold text-foreground-800">其他读者：</span>
                            {r}
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="mt-3 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => toggleLike(idx)}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                          isLiked
                            ? "border-accent-400 bg-accent-100 text-accent-800"
                            : "border-background-200 text-foreground-500 hover:border-accent-300"
                        }`}
                      >
                        <i className={isLiked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
                        {c.likes}
                      </button>
                      <button
                        type="button"
                        onClick={() => setOpenReply(openReply === idx ? null : idx)}
                        className="inline-flex items-center gap-1 rounded-full border border-background-200 px-2.5 py-1 text-xs text-foreground-500 transition-colors hover:border-foreground-300"
                      >
                        <i className="ri-reply-line"></i>
                        回复 {c.replies}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}